// Copyright 2019 Drivetrain Hub LLC
// Functions for spatial coordinate calculations.

import math from "./math";
import { objToArr } from "./utils";

// region HELPERS
// https://gist.github.com/jremmen/9454479
// mmultiply is a passive transform

const mmultiply = (a, b) => a.map(x => transpose(b).map(y => dotproduct(x, y)));
const dotproduct = (a, b) => a.map((x, i) => a[i] * b[i]).reduce((m, n) => m + n);
const transpose = a => a[0].map((x, i) => a.map(y => y[i]));

// endregion

// region COORDINATE SYSTEM CONVERSIONS

function cartesianToCylindrical(x, y, z, unitAngle='rad') {
  let r = Math.hypot(x, y);
  let phi = Math.atan2(y, x);
  let zz = z*1;  // makes form validation behavior same as r, phi

  phi = math.angle2deg(phi, unitAngle);

  return [r, phi, zz];
}

function cylindricalToCartesian(r, phi, z, unitAngle='rad') {
  phi = math.angle2rad(phi, unitAngle);

  let x = r * Math.cos(phi);
  let y = r * Math.sin(phi);
  let zz = z*1;  // makes form validation behavior same as x, y

  return [x, y, zz];
}

function cartesianToSpherical(x, y, z, unitAngle='rad') {
  let r_cyl = Math.hypot(x, y);

  let r = Math.hypot(r_cyl, z);
  let phi = Math.atan2(y, x);
  let theta = Math.atan2(Math.abs(r_cyl), z);

  phi = math.angle2deg(phi, unitAngle);
  theta = math.angle2deg(theta, unitAngle);

  return [r, phi, theta];
}

function sphericalToCartesian(r, phi, theta, unitAngle='rad') {
  phi = math.angle2rad(phi, unitAngle);
  theta = math.angle2rad(theta, unitAngle);

  let x = r * Math.sin(theta) * Math.cos(phi);
  let y = r * Math.sin(theta) * Math.sin(phi);
  let z = r * Math.cos(theta);

  return [x, y, z];
}

function cylindricalToSpherical(r, phi, z, unitAngle='rad') {
  let [x, y] = cylindricalToCartesian(r, phi, z, unitAngle);

  return cartesianToSpherical(x, y, z, unitAngle);
}

function sphericalToCylindrical(r, phi, theta, unitAngle='rad') {
  let [x, y, z] = sphericalToCartesian(r, phi, theta, unitAngle);

  return cartesianToCylindrical(x, y, z, unitAngle);
}

const conversionFunctions = {
  '12': cartesianToCylindrical,
  '13': cartesianToSpherical,
  '21': cylindricalToCartesian,
  '23': cylindricalToSpherical,
  '31': sphericalToCartesian,
  '32': sphericalToCylindrical,
};

/**
 * Convert coordinates between Cartesian, cylindrical, and spherical systems.
 *
 * @param {Array || Object} coord - Coordinates array or object to convert, ex) [1, 2, 3] or {a: '1', b: '2', c: '3'}.
 *  `coord` values can be Number or String type.
 * @param {string} fromSys - From coordinate system, specified by numeric string, ex) '1'.
 * @param {string} toSys - To coordinate system, specified by numeric string, ex) '2'.
 * @param {string} unitAngle - Units of any angular values that input and output must adhere to.
 * @return {Array} Returns an array with Number type values of the converted coordinates, ex) [1, 2, 3].
 */
function convertCoord(coord, fromSys, toSys, unitAngle='rad') {

  if (typeof coord === 'object') {
    coord = objToArr(coord);
  }

  // convert all elements to number type
  coord = coord.map(el => Number(el));

  if (fromSys === toSys) {
    return coord;
  } else {
    const conversion = fromSys + toSys;
    return conversionFunctions[conversion](...coord, unitAngle);
  }
}

// endregion

// region ROTATION TRANSFORMS

export function rotationMatrix2(phi) {
  const cos = Math.cos(phi);
  const sin = Math.sin(phi);

  return [
    [cos, -sin],
    [sin, cos]
  ];
}

/**
 * Matrix multiplication with 2x2 rotation matrix and (x, y) coordinates.
 * @param {array} rotMatrix - 2x2 rotation matrix.
 * @param {number} x0 - x-coordinate of point to rotate
 * @param {number} y0 - y-coordinate of point to rotate.
 * @return {{x: number, y: number}} - Coordinates of rotated point.
 */
export function rotatePoint2(rotMatrix, {x: x0, y: y0}) {

  const x = rotMatrix[0][0] * x0 + rotMatrix[0][1] * y0;
  const y = rotMatrix[1][0] * x0 + rotMatrix[1][1] * y0;

  return {x, y};
}

export function rotatePoints2(rotMatrix, xArr, yArr) {
  // Returns new coordinate arrays of transformed coordinates.

  let xNew = [];
  let yNew = [];

  for (let i=0; i < xArr.length; i++) {
    let {x, y} = rotatePoint2(rotMatrix, {x: xArr[i], y: yArr[i]});
    xNew.push(x);
    yNew.push(y);
  }

  return [xNew, yNew]
}

/**
 * Mirror plane (x, y) coordinates about x-axis.
 * @param {Array} xyObjects - Array of objects with (x, y) properties.
 * @return {Array} - Array of objects with (x, y) properties mirrored about x-axis.
 */
export function mirrorAboutX(xyObjects) {

  let xyObjectsMirrored = [];
  let obj;

  for (let i=0; i < xyObjects.length; i++) {
    obj = Object.assign({}, xyObjects[i]);  // clone object
    obj.y *= -1;  // mirror about x-axis
    xyObjectsMirrored.push(obj);
  }

  return xyObjectsMirrored;
}

/**
 * Rotate coordinate system per rotation matrix.
 * @param rotMatrix {[[number], ...]} - Coordinate transform rotation matrix, 2-D array (3x3).
 * @param matrix {[[number], ...]} - Coordinate system axes, 2-D array (3x3).
 * @return {[[number], ...]} Rotated coordinate system matrix, 2-D array (3x3).
 */
export function rotateMatrix3(rotMatrix, matrix) {
  return mmultiply(rotMatrix, matrix);
}

/**
 * Get rotation matrix required to transform from one axes to another within same coordinate frame, e.g. global.
 * @param toAxes {[[number], ...]} - Axes to rotate *to*, 2-D array (3x3).
 * @param fromAxes {[[number], ...]} - Axes to rotate *from*, 2-D array (3x3).
 * @return {[[number], ...]}  Rotation matrix, 2-D array (3x3).
 */
export function getRotationMatrix3(toAxes, fromAxes) {
  return mmultiply(toAxes, transpose(fromAxes))
}

// endregion

// region FORMATTING

export function points(xArr, yArr) {
  // Get an array of {x, y} objects from arrays of x and y.

  return xArr.map((x, i) => {return {x: x, y: yArr[i]}})
}

export function points2Arrays(pts) {
  // Transform an array of {x, y} objects to arrays of x and y.

  let x=[], y=[];
  pts.forEach(pt => {
    x.push(pt.x);
    y.push(pt.y);
  });

  return [x, y]
}

export function formatCoordStr(arr, digits=3) {
  // Format a coordinate array for display as string:  '(x, y, z)'
  // Input array can be of String or Number type.

  // ensure array is of number type for use with round()
  arr = arr.map(Number);

  let arrRound = [];
  for (let i=0; i < arr.length; i++) {
    arrRound.push(math.round(arr[i], digits));
  }

  return '(' + arrRound.join(', ') + ')';
}

export function labelNameAtCoord(name, coord, digits=3) {
  // Format a label for displaying part name with its coordinate:  'name @ (x, y, z)'
  // If name arg is null, then null is returned.

  const coordStr = formatCoordStr(coord, digits);

  if (name !== null) {
    return name + ' @ ' + coordStr;
  } else {
    return null;
  }
}

// endregion

export default {
  // CONVERSIONS
  cartesianToCylindrical,
  cylindricalToCartesian,
  convertCoord,

  // ROTATION TRANSFORMS
  rotationMatrix2: rotationMatrix2,
  rotatePoints2: rotatePoints2,

  // FORMATTING
  points: points,
  formatCoordStr,
  labelNameAtCoord,
}
